/**
 * API Service
 * Handles all HTTP requests to the Admin API
 */

import axios from 'axios';
import { API_BASE_URL, STORAGE_KEYS } from '../utils/constants';

// Create axios instance with default config
const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json'
  }
});

// Request interceptor - add JWT token to all requests
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem(STORAGE_KEYS.AUTH_TOKEN);
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor - handle auth errors globally
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Unauthorized - clear auth and redirect to login
      localStorage.removeItem(STORAGE_KEYS.AUTH_TOKEN);
      localStorage.removeItem(STORAGE_KEYS.USER);
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// ==============================================================================
// AUTH ENDPOINTS
// ==============================================================================

export const authAPI = {
  /**
   * Login with username and password
   * @param {string} username
   * @param {string} password
   * @returns {Promise<{token: string, expiresIn: number}>}
   */
  login: async (username, password) => {
    const response = await api.post('/admin/auth/login', { username, password });
    return response.data;
  },

  /**
   * Generate password hash (development only)
   * @param {string} password
   * @returns {Promise<{hash: string}>}
   */
  generateHash: async (password) => {
    const response = await api.post('/admin/auth/generate-hash', { password });
    return response.data;
  }
};

// ==============================================================================
// GAMES ENDPOINTS
// ==============================================================================

export const gamesAPI = {
  /**
   * Get all games with optional filtering
   * @param {Object} params - Query parameters
   * @param {string} params.status - Filter by status
   * @param {string} params.language - Filter by language
   * @param {string} params.search - Search by name or code
   * @param {number} params.limit - Page size (default 50)
   * @param {number} params.offset - Pagination offset (default 0)
   * @returns {Promise<{games: Array, totalCount: number, limit: number, offset: number}>}
   */
  getAll: async (params = {}) => {
    const response = await api.get('/admin/games', { params });
    return response.data;
  },

  /**
   * Get game by ID (includes full gameData JSON)
   * @param {number} id - Game ID
   * @returns {Promise<Object>} Game object with full gameData
   */
  getById: async (id) => {
    const response = await api.get(`/admin/games/${id}`);
    return response.data;
  },

  /**
   * Get game by gameCode and language
   * @param {string} gameCode - Game code
   * @param {string} language - Language code (default 'en')
   * @returns {Promise<Object|null>} Game object or null if not found
   */
  getByCode: async (gameCode, language = 'en') => {
    const response = await api.get('/admin/games', {
      params: { search: gameCode, language, limit: 10 }
    });
    // Find exact match by gameCode
    const games = response.data.games || [];
    return games.find(g => g.gameCode === gameCode && g.language === language) || null;
  },

  /**
   * Create new game
   * @param {Object} gameData - Game data
   * @returns {Promise<Object>} Created game
   */
  create: async (gameData) => {
    const response = await api.post('/admin/games', gameData);
    return response.data;
  },

  /**
   * Update existing game
   * @param {number} id - Game ID
   * @param {Object} gameData - Updated game data
   * @returns {Promise<Object>} Updated game
   */
  update: async (id, gameData) => {
    const response = await api.put(`/admin/games/${id}`, gameData);
    return response.data;
  },

  /**
   * Delete game (soft delete - sets status to 'deleted')
   * @param {number} id - Game ID
   * @returns {Promise<{message: string, id: number}>}
   */
  delete: async (id) => {
    const response = await api.delete(`/admin/games/${id}`);
    return response.data;
  },

  /**
   * Change game status (with validation)
   * @param {number} id - Game ID
   * @param {string} newStatus - New status value
   * @param {string|null} targetChannel - Optional target release channel (for ready_for_prod)
   * @returns {Promise<Object>} Updated game
   */
  transitionStatus: async (id, newStatus, targetChannel = null) => {
    const payload = { newStatus };
    if (targetChannel) {
      payload.targetChannel = targetChannel;
    }
    const response = await api.post(`/admin/games/${id}/transition`, payload);
    return response.data;
  },

  /**
   * Cancel pending modifications and restore production version
   * @param {number} id - Game ID
   * @returns {Promise<Object>} Updated game (restored from production)
   */
  cancelModifications: async (id) => {
    const response = await api.post(`/admin/games/${id}/cancel-modifications`);
    return response.data;
  },

  /**
   * Get beta feedback for a game
   * @param {number} id - Game ID
   * @returns {Promise<{gameCode: string, language: string, feedback: Array, summary: Object}>}
   */
  getFeedback: async (id) => {
    const response = await api.get(`/admin/games/${id}/feedback`);
    return response.data;
  },

  /**
   * Copy an existing game to create variant, expansion, or new edition
   * @param {Object} data - Copy configuration
   * @param {number} data.sourceId - ID of source game to copy
   * @param {string} data.newGameCode - Game code for the new game
   * @param {string} data.newGameName - Display name for the new game
   * @param {string} data.creationType - 'variant' | 'expansion' | 'new_edition'
   * @param {string} data.variantBehavior - Optional: for variants only
   * @param {number} data.expansionOrder - Optional: for expansions only
   * @returns {Promise<{message: string, game: Object}>}
   */
  copy: async (data) => {
    const response = await api.post('/admin/games/copy', data);
    return response.data;
  }
};

// ==============================================================================
// SUBMISSIONS ENDPOINTS
// ==============================================================================

export const submissionsAPI = {
  /**
   * Get all submissions with optional filtering
   * @param {Object} params - Query parameters
   * @param {string} params.status - Filter by submission status
   * @param {string} params.search - Search by game name or submitter
   * @param {number} params.limit - Page size (default 50)
   * @param {number} params.offset - Pagination offset (default 0)
   * @returns {Promise<{submissions: Array, totalCount: number, limit: number, offset: number}>}
   */
  getAll: async (params = {}) => {
    const response = await api.get('/admin/submissions', { params });
    return response.data;
  },

  /**
   * Get submission by ID (includes full submission data)
   * @param {number} id - Submission ID
   * @returns {Promise<Object>} Submission object with full data
   */
  getById: async (id) => {
    const response = await api.get(`/admin/submissions/${id}`);
    return response.data;
  },

  /**
   * Accept a submission (creates game in catalog)
   * @param {number} id - Submission ID
   * @param {Object} data - Optional acceptance data
   * @param {string} data.releaseChannel - Target release channel
   * @param {string} data.notes - Admin notes
   * @returns {Promise<Object>} Created game info
   */
  accept: async (id, data = {}) => {
    const response = await api.post(`/admin/submissions/${id}/accept`, data);
    return response.data;
  },

  /**
   * Reject a submission
   * @param {number} id - Submission ID
   * @param {Object} data - Rejection data
   * @param {string} data.reason - Rejection reason
   * @param {string} data.notes - Additional notes for submitter
   * @returns {Promise<Object>} Updated submission
   */
  reject: async (id, data) => {
    const response = await api.post(`/admin/submissions/${id}/reject`, data);
    return response.data;
  },

  /**
   * Request additional information from submitter
   * @param {number} id - Submission ID
   * @param {Object} data - Request data
   * @param {string} data.questions - Questions/info needed
   * @returns {Promise<Object>} Updated submission
   */
  requestInfo: async (id, data) => {
    const response = await api.post(`/admin/submissions/${id}/request-info`, data);
    return response.data;
  },

  /**
   * Update submission status directly
   * @param {number} id - Submission ID
   * @param {string} status - New status value
   * @param {string} notes - Optional notes
   * @returns {Promise<Object>} Updated submission
   */
  updateStatus: async (id, status, notes = '') => {
    const response = await api.patch(`/admin/submissions/${id}/status`, { status, admin_notes: notes });
    return response.data;
  }
};

// ==============================================================================
// CONTRIBUTORS ENDPOINTS
// ==============================================================================

export const contributorsAPI = {
  /**
   * Get all contributors with optional filtering
   * @param {Object} params - Query parameters
   * @param {string} params.status - Filter by status (pending, activated, disabled, deleted)
   * @param {string} params.pending_approval - 'true' to show only those awaiting public listing
   * @param {string} params.search - Search by handle or email
   * @param {number} params.limit - Page size (default 50)
   * @param {number} params.offset - Pagination offset (default 0)
   * @returns {Promise<{contributors: Array, totalCount: number, limit: number, offset: number}>}
   */
  getAll: async (params = {}) => {
    const response = await api.get('/admin/contributors', { params });
    return response.data;
  },

  /**
   * Get contributor statistics
   * @returns {Promise<{total: number, activated: number, pending: number, disabled: number, deleted: number, pending_approval: number}>}
   */
  getStats: async () => {
    const response = await api.get('/admin/contributors/stats');
    return response.data;
  },

  /**
   * Get contributor by ID with full details
   * @param {number} id - Contributor ID
   * @returns {Promise<Object>} Contributor with submissions and published_games
   */
  getById: async (id) => {
    const response = await api.get(`/admin/contributors/${id}`);
    return response.data;
  },

  /**
   * Update contributor settings
   * @param {number} id - Contributor ID
   * @param {Object} data - Update data
   * @param {boolean} data.isPubliclyVisible - Approve for public listing
   * @param {string} data.status - Change status (activated, disabled)
   * @returns {Promise<{message: string, contributor: Object}>}
   */
  update: async (id, data) => {
    const response = await api.patch(`/admin/contributors/${id}`, data);
    return response.data;
  },

  /**
   * Approve contributor for public listing
   * @param {number} id - Contributor ID
   * @returns {Promise<{message: string, handle: string}>}
   */
  approvePublic: async (id) => {
    const response = await api.post(`/admin/contributors/${id}/approve-public`);
    return response.data;
  },

  /**
   * Reject contributor for public listing
   * @param {number} id - Contributor ID
   * @param {string} reason - Optional rejection reason
   * @returns {Promise<{message: string, handle: string}>}
   */
  rejectPublic: async (id, reason = '') => {
    const response = await api.post(`/admin/contributors/${id}/reject-public`, { reason });
    return response.data;
  }
};

// ==============================================================================
// DASHBOARD ENDPOINTS
// ==============================================================================

export const dashboardAPI = {
  /**
   * Get aggregated dashboard statistics
   * @returns {Promise<{games: Object, submissions: Object, contributors: Object, recentActivity: Array}>}
   */
  getStats: async () => {
    const response = await api.get('/admin/dashboard/stats');
    return response.data;
  }
};

// ==============================================================================
// HEALTH CHECK
// ==============================================================================

export const healthAPI = {
  /**
   * Check if Admin API is responding
   * @returns {Promise<Object>}
   */
  check: async () => {
    const response = await api.get('/health');
    return response.data;
  }
};

// Export configured axios instance for custom requests
export default api;
